import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  MapPin, Loader2, RefreshCw, Truck, User, Clock, 
  Navigation, AlertCircle, Signal, SignalZero
} from 'lucide-react';
import { toast } from 'sonner';
import { useLocationTracking } from '../hooks/useLocationTracking';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Van SVG icon for Google Maps marker
const VAN_ICON_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%COLOR%" width="40" height="40">
  <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5H6.5C5.84 5 5.28 5.42 5.08 6.01L3 12V20C3 20.55 3.45 21 4 21H5C5.55 21 6 20.55 6 20V19H18V20C18 20.55 18.45 21 19 21H20C20.55 21 21 20.55 21 20V12L18.92 6.01ZM6.5 16C5.67 16 5 15.33 5 14.5C5 13.67 5.67 13 6.5 13C7.33 13 8 13.67 8 14.5C8 15.33 7.33 16 6.5 16ZM17.5 16C16.67 16 16 15.33 16 14.5C16 13.67 16.67 13 17.5 13C18.33 13 19 13.67 19 14.5C19 15.33 18.33 16 17.5 16ZM5 11L6.5 6.5H17.5L19 11H5Z"/>
</svg>
`;

// Generate colored van icon URL
const getVanIconUrl = (color) => {
  const svg = VAN_ICON_SVG.replace('%COLOR%', encodeURIComponent(color));
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

// Worker colors for different workers
const WORKER_COLORS = [
  '#10b981', // emerald
  '#3b82f6', // blue
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
];

const getWorkerColor = (index) => WORKER_COLORS[index % WORKER_COLORS.length];

// Format time ago
const formatTimeAgo = (isoString) => {
  if (!isoString) return 'Unknown';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  
  if (diffSecs < 60) return 'Just now';
  if (diffMins < 2) return '1 minute ago';
  if (diffMins < 60) return `${diffMins} minutes ago`;
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const WorkerLocations = ({ onBack, user }) => {
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState(null);
  
  // Persistent tracking via the shared hook — survives PWA close/reopen.
  const { tracking: isTracking, enable: enableTracking, disable: disableTracking, permissionDenied } = useLocationTracking();
  
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const infoWindowRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const trackingWatchRef = useRef(null);
  
  // Current user role
  const isWorker = user?.role === 'Worker';
  
  // Load Google Maps API
  useEffect(() => {
    if (window.google && window.google.maps) {
      setMapLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setMapLoaded(true);
    script.onerror = () => setError('Failed to load Google Maps');
    document.head.appendChild(script);
  }, []);

  // Fetch worker locations
  const fetchLocations = useCallback(async () => {
    try {
      const token = localStorage.getItem('streakfree_token');
      if (!token) return;
      
      const response = await fetch(`${API_URL}/api/team/locations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLocations(data.locations || []);
        setLastRefresh(new Date());
        setError(null);
      } else {
        setError('Failed to fetch locations');
      }
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError('Network error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch and auto-refresh every 10 seconds
  useEffect(() => {
    fetchLocations();
    
    refreshIntervalRef.current = setInterval(fetchLocations, 10000);
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [fetchLocations]);

  // Start/stop location tracking — delegated to the persistent hook so it
  // survives PWA close/reopen. The hook handles geolocation + POSTing to
  // /api/team/location + persisting the opt-in flag in localStorage.
  const toggleTracking = useCallback(async () => {
    if (isTracking) {
      await disableTracking();
      toast.success('Location sharing stopped');
    } else {
      if (!navigator.geolocation) {
        toast.error('Geolocation is not supported by your browser');
        return;
      }
      // Prime the permission prompt explicitly before enabling, so the
      // toast/error feedback feels immediate.
      navigator.geolocation.getCurrentPosition(
        () => {
          enableTracking();
          toast.success('Location sharing started — will auto-resume when you reopen the app');
          // Refresh the locations list so we see ourselves immediately.
          setTimeout(fetchLocations, 1500);
        },
        (err) => {
          if (err.code === 1) {
            toast.error('Location permission denied. Please enable location access in your browser settings.');
          } else {
            toast.error('Could not get your location');
          }
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, [isTracking, enableTracking, disableTracking, fetchLocations]);

  // Cleanup is handled by the useLocationTracking hook itself.
  useEffect(() => {
    return () => {};
  }, []);

  // Initialize and update map
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;

    // Create map if not exists
    if (!mapInstanceRef.current) {
      // Default center to Sussex, UK
      const defaultCenter = { lat: 50.9256, lng: -0.1456 };
      
      const map = new window.google.maps.Map(mapRef.current, {
        center: defaultCenter,
        zoom: 11,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        styles: [
          { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }
        ]
      });

      mapInstanceRef.current = map;
      infoWindowRef.current = new window.google.maps.InfoWindow();
    }

    const map = mapInstanceRef.current;

    // Update markers
    const currentWorkerIds = new Set(locations.map(l => l.worker_id));
    
    // Remove markers for workers no longer in locations
    Object.keys(markersRef.current).forEach(workerId => {
      if (!currentWorkerIds.has(workerId)) {
        markersRef.current[workerId].setMap(null);
        delete markersRef.current[workerId];
      }
    });

    // Add/update markers for each worker
    locations.forEach((location, index) => {
      const position = { lat: location.lat, lng: location.lng };
      const color = getWorkerColor(index);
      
      if (markersRef.current[location.worker_id]) {
        // Update existing marker position
        markersRef.current[location.worker_id].setPosition(position);
      } else {
        // Create new marker
        const marker = new window.google.maps.Marker({
          position: position,
          map: map,
          icon: {
            url: getVanIconUrl(color),
            scaledSize: new window.google.maps.Size(40, 40),
            anchor: new window.google.maps.Point(20, 20)
          },
          title: location.worker_name
        });

        marker.addListener('click', () => {
          setSelectedWorker(location);
          
          const content = `
            <div style="font-family: system-ui; padding: 8px; min-width: 150px;">
              <div style="font-weight: 600; font-size: 14px; color: #18181b; margin-bottom: 4px;">
                ${location.worker_name}
              </div>
              <div style="font-size: 12px; color: #71717a;">
                Updated ${formatTimeAgo(location.updated_at)}
              </div>
              ${location.speed ? `<div style="font-size: 12px; color: #71717a; margin-top: 4px;">Speed: ${Math.round(location.speed * 2.237)} mph</div>` : ''}
            </div>
          `;
          
          infoWindowRef.current.setContent(content);
          infoWindowRef.current.open(map, marker);
        });

        markersRef.current[location.worker_id] = marker;
      }
    });

    // Fit bounds if we have locations
    if (locations.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      locations.forEach(location => {
        bounds.extend({ lat: location.lat, lng: location.lng });
      });
      
      // Only fit bounds if there's more than one location or it's the first load
      if (locations.length > 1) {
        map.fitBounds(bounds, { padding: 50 });
      } else if (locations.length === 1) {
        map.setCenter({ lat: locations[0].lat, lng: locations[0].lng });
        map.setZoom(14);
      }
    }
  }, [mapLoaded, locations]);

  return (
    <div className="h-full flex flex-col bg-zinc-50" data-testid="worker-locations-page">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-xl">
              <Truck size={24} className="text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-zinc-900">Worker Locations</h1>
              <p className="text-sm text-zinc-500">Live tracking of your team</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Share Location Toggle */}
            <button
              onClick={toggleTracking}
              data-testid="toggle-tracking-btn"
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-colors ${
                isTracking 
                  ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                  : 'bg-emerald-500 text-white hover:bg-emerald-600'
              }`}
            >
              {isTracking ? (
                <>
                  <SignalZero size={18} />
                  Stop Sharing
                </>
              ) : (
                <>
                  <Signal size={18} />
                  Share My Location
                </>
              )}
            </button>
            
            {/* Refresh Button */}
            <button
              onClick={fetchLocations}
              data-testid="refresh-btn"
              className="flex items-center gap-2 px-3 py-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-xl transition-colors"
            >
              <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
        
        {/* Status bar */}
        <div className="flex items-center gap-4 mt-3 text-sm">
          <div className="flex items-center gap-1.5 text-zinc-600">
            <User size={14} />
            <span>{locations.length} worker{locations.length !== 1 ? 's' : ''} online</span>
          </div>
          {lastRefresh && (
            <div className="flex items-center gap-1.5 text-zinc-500">
              <Clock size={14} />
              <span>Updated {formatTimeAgo(lastRefresh.toISOString())}</span>
            </div>
          )}
          {isTracking && (
            <div className="flex items-center gap-1.5 text-emerald-600">
              <Signal size={14} className="animate-pulse" />
              <span>Sharing your location</span>
            </div>
          )}
        </div>
      </div>

      {/* Main content - vertical layout: Workers list ON TOP, map BELOW */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Workers list - top */}
        <div className="bg-white border-b border-zinc-200 overflow-y-auto max-h-[40vh] flex-shrink-0">
          <div className="p-3 border-b border-zinc-100">
            <h3 className="text-sm font-semibold text-zinc-800">Active Workers</h3>
          </div>
          
          {locations.length === 0 ? (
            <div className="p-4 text-center text-zinc-500 text-sm">
              No workers currently sharing location
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {locations.map((location, index) => (
                <button
                  key={location.worker_id}
                  onClick={() => {
                    setSelectedWorker(location);
                    if (mapInstanceRef.current) {
                      mapInstanceRef.current.setCenter({ lat: location.lat, lng: location.lng });
                      mapInstanceRef.current.setZoom(15);
                      
                      // Open info window
                      if (markersRef.current[location.worker_id] && infoWindowRef.current) {
                        const content = `
                          <div style="font-family: system-ui; padding: 8px; min-width: 150px;">
                            <div style="font-weight: 600; font-size: 14px; color: #18181b; margin-bottom: 4px;">
                              ${location.worker_name}
                            </div>
                            <div style="font-size: 12px; color: #71717a;">
                              Updated ${formatTimeAgo(location.updated_at)}
                            </div>
                          </div>
                        `;
                        infoWindowRef.current.setContent(content);
                        infoWindowRef.current.open(mapInstanceRef.current, markersRef.current[location.worker_id]);
                      }
                    }
                  }}
                  data-testid={`worker-${location.worker_id}`}
                  className={`w-full flex items-center gap-3 p-3 hover:bg-zinc-50 transition-colors text-left ${
                    selectedWorker?.worker_id === location.worker_id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: getWorkerColor(index) + '20' }}
                  >
                    <Truck size={20} style={{ color: getWorkerColor(index) }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-zinc-900 truncate">{location.worker_name}</p>
                    <p className="text-xs text-zinc-500">{formatTimeAgo(location.updated_at)}</p>
                  </div>
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" title="Online" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Map - below the workers list */}
        <div className="flex-1 relative min-h-[40vh]">
          {isLoading && !mapLoaded ? (
            <div className="absolute inset-0 flex items-center justify-center bg-zinc-100">
              <div className="text-center">
                <Loader2 size={32} className="animate-spin text-blue-500 mx-auto mb-2" />
                <p className="text-zinc-600">Loading map...</p>
              </div>
            </div>
          ) : error ? (
            <div className="absolute inset-0 flex items-center justify-center bg-zinc-100">
              <div className="text-center">
                <AlertCircle size={32} className="text-red-500 mx-auto mb-2" />
                <p className="text-zinc-600">{error}</p>
              </div>
            </div>
          ) : null}
          
          <div ref={mapRef} className="w-full h-full" data-testid="locations-map" />
          
          {/* Empty state overlay */}
          {!isLoading && locations.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center bg-zinc-100/80 pointer-events-none">
              <div className="text-center p-6 bg-white rounded-2xl shadow-lg pointer-events-auto max-w-sm">
                <Truck size={48} className="text-zinc-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-zinc-800 mb-2">No Workers Online</h3>
                <p className="text-sm text-zinc-500 mb-4">
                  Workers need to click "Share My Location" to appear on the map.
                </p>
                {!isTracking && (
                  <button
                    onClick={toggleTracking}
                    className="px-4 py-2 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition-colors"
                  >
                    Share My Location
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkerLocations;
